import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isEmptyCustomActivity } from '../Blocks/CustomActivityV2/emptyActivity';
import { ReadingStrategiesIcon } from '../Icons/ReadingStrategiesIcon';
import { WritingStrategiesIcon } from '../Icons/WritingStrategiesIcon';
import { SummarizeLearningIcon } from '../Icons/SummarizeLearningIcon';
import { TextAnalysisIcon } from '../Icons/TextAnalysisIcon';
import { CollaborationIcon } from '../Icons/CollaborationIcon';
import { CriticalThinkingIcon } from '../Icons/CriticalThinkingIcon';
import { VocabularyIcon } from '../Icons/VocabularyIcon';
import { QuestionsIcon } from '../Icons/QuestionsIcon';

// Constants
export const SLOW_IMAGE_MESSAGE = 'Loading images... may take a minute.';

export const LOADING_MESSAGE = {
  key_vocab_list_with_images: SLOW_IMAGE_MESSAGE,
  key_vocab_list_with_images_slides: SLOW_IMAGE_MESSAGE,
  vocabulary_match_and_explain: SLOW_IMAGE_MESSAGE,
  vocabulary_match_and_explain_slides: SLOW_IMAGE_MESSAGE,
  vocabulary_match_the_image: SLOW_IMAGE_MESSAGE,
};

export const QUESTION_TYPES = {
  SAQ: 'saq',
  MCQ: 'mcq',
  OEQ: 'oeq',
};

export const LOADING_BLOCK = {
  canonical_id: 'loading',
  unique_id: 'loading_' + uuidv4(),
  resource_data: {},
  template_data: {},
};

// The most activities a resource can hold. diffit-api enforces the same limit
// (MAX_BLOCKS_PER_PACKET): a save that adds activities past it is refused.
export const MAX_BLOCK_COUNT_PROJECTS = 30;
export const SAVE_DEBOUNCE_MS = 1000; // 1 second

// Helper functions

/**
 * Remove nested groups from custom_activity blocks.
 * Flattens sections with type=group in custom_activity_v2 or custom_activity_slides_v2 blocks.
 */
export const removeNestedGroups = (packet) => {
  if (!packet?.blocks?.length) {
    return packet;
  }
  const flattenedBlocks = packet.blocks.map((block) => {
    if (
      block.canonical_id === 'custom_activity_v2' ||
      block.canonical_id === 'custom_activity_slides_v2'
    ) {
      return {
        ...block,
        resource_data: {
          ...block.resource_data,
          custom_content: {
            ...block.resource_data.custom_content,
            items: block.resource_data.custom_content.items.map((item) => ({
              ...item,
              structure: {
                ...item.structure,
                sections: item.structure.sections.flatMap((section) =>
                  section.type === 'group' ? section.children : [section]
                ),
              },
            })),
          },
        },
      };
    }
    return block;
  });
  return {
    ...packet,
    blocks: flattenedBlocks,
  };
};

/**
 * Clean loading objects from packet before saving to server or putting in undo/redo stack.
 * Removes:
 * - Blocks with canonical_id === 'loading'
 * - Items with is_loading flag in resource_data arrays
 * - Custom activity sections with _generating flag (rich content generation placeholders)
 * - Custom activities with nothing left to render (every section deleted): an empty activity is
 *   a deleted one. Runs on load too, so packets saved empty before removeSection deleted the
 *   activity open without it, and their next save drops it.
 */
export const cleanLoadingObjectsFromPacket = (packet) => {
  if (!packet?.blocks?.length) {
    return packet;
  }
  const cleanedPacket = {
    ...packet,
    blocks: packet.blocks
      ?.filter((block) => block.canonical_id !== 'loading')
      .map((block) => {
        // No resource data, no need to clean
        if (!block.resource_data) {
          return block;
        }
        const cleanedBlock = {
          ...block,
          resource_data: { ...block.resource_data },
        };
        // iterate through block.resource_data keys and remove any loading objects
        for (const key in block.resource_data) {
          if (block.resource_data[key].items) {
            const notLoadingItems = block.resource_data[key].items
              .filter((item) => !item?.is_loading)
              .map((item) => {
                // Strip _generating sections from custom activity structures
                const sections = item?.structure?.sections;
                if (!Array.isArray(sections)) return item;
                const filteredSections = sections.filter((s) => !s?._generating);
                if (filteredSections.length === sections.length) return item;
                return {
                  ...item,
                  structure: { ...item.structure, sections: filteredSections },
                };
              });
            cleanedBlock.resource_data[key] = {
              ...cleanedBlock.resource_data[key],
              items: notLoadingItems,
            };
          }
        }
        return cleanedBlock;
      })
      .filter((block) => !isEmptyCustomActivity(block)),
  };
  return cleanedPacket;
};

/**
 * Create a loading block placeholder for an activity.
 */
export const createLoadingBlock = (activity) => {
  return {
    canonical_id: 'loading',
    unique_id: activity.specified_id || 'loading_' + uuidv4(),
    resource_data: {},
    template_data: {
      title: {
        text: activity.title || 'Loading...',
      },
      loading_message: LOADING_MESSAGE[activity.canonical_id],
    },
  };
};

/**
 * Internal helper for repeating rows instantiation.
 */
const _doRepeatingRowsInstantiation = (block, key) => {
  const repeatingRows = block[key]?.repeating_rows;
  if (!repeatingRows) {
    return;
  }

  for (let i = 0; i < repeatingRows.length; i++) {
    const repeatingRow = repeatingRows[i];
    let count;
    if (repeatingRow.data_key) {
      const dataKey = repeatingRow.data_key;
      const data = block.resource_data?.[dataKey];
      if (dataKey && !data?.items?.length) {
        console.error(
          'Repeating row error: Resource data not found:',
          dataKey,
          'for block',
          block.canonical_id
        );
        continue;
      }
      count = data.items.length;
    } else {
      count = repeatingRow.count;
    }
    if (count === undefined) {
      console.error(
        `Repeating row error: Could not determine the number of rows to render for block ${block.canonical_id}. Please check that either count or data_key fields are set. Removing block.`
      );
      continue;
    }

    let rows = repeatingRow.rows || [];
    if (rows.length === count) {
      return;
    }
    if (rows.length > count) {
      rows = rows.slice(0, count);
      return;
    }
    for (let j = 0; j < count; j++) {
      if (j < rows.length) {
        continue;
      }
      const newRow = {};
      for (const rowKey in repeatingRow.row_template) {
        const text = repeatingRow.row_template?.[rowKey]?.text?.replace('{{index}}', j + 1);
        newRow[rowKey] = { text: text };
      }
      rows.push(newRow);
    }
    block[key].repeating_rows[i].rows = rows;
  }
};

/**
 * Instantiate repeating rows in a block for both template_data and student_response_data.
 */
export const instantiateRepeatingRows = (block) => {
  _doRepeatingRowsInstantiation(block, 'template_data');
  _doRepeatingRowsInstantiation(block, 'student_response_data');
};

/**
 * Remove citations from reading text.
 * A simple regex sometimes has issues with catastrophic backtracking,
 * so this is a slower but more robust solution.
 */
export const removeCitationsFromReading = (text) => {
  if (!text) return '';

  const chunks = text.split('[');
  if (chunks.length === 1) return text; // No citations found

  return chunks
    .map((chunk, index) => {
      if (index === 0) return chunk; // First chunk before any '['

      const closeBracketIndex = chunk.indexOf(']');
      if (closeBracketIndex === -1) return '[' + chunk; // No closing bracket, return as-is

      // Check if what's between [ and ] is a number
      const citation = chunk.slice(0, closeBracketIndex);
      if (!/^\d+$/.test(citation)) return '[' + chunk; // Not a number, return as-is

      return chunk.slice(closeBracketIndex + 1).trim(); // Return everything after the ] and trim whitespace
    })
    .join('')
    .replaceAll(' .', '.')
    .trim(); // Remove leading/trailing whitespace
};

/**
 * Get icon component for a block category.
 */
export const getIconForBlockCategory = (category) => {
  switch (category) {
    case 'Reading Strategies':
      return ReadingStrategiesIcon;
    case 'Writing Strategies':
      return WritingStrategiesIcon;
    case 'Summarize Learning':
      return SummarizeLearningIcon;
    case 'Text Analysis':
      return TextAnalysisIcon;
    case 'Collaboration':
      return CollaborationIcon;
    case 'Critical Thinking':
      return CriticalThinkingIcon;
    case 'Vocabulary':
      return VocabularyIcon;
    case 'Questions':
      return QuestionsIcon;
    default:
      return null;
  }
};

/**
 * Get icon component for a block type.
 */
export const getIconForBlockType = (type) => {
  switch (type) {
    case 'reading':
      return ReadingStrategiesIcon;
    case 'vocabulary':
      return VocabularyIcon;
    case 'questions':
      return QuestionsIcon;
    case 'custom':
      return SummarizeLearningIcon;
    default:
      return null;
  }
};

/**
 * Get the unique_id of the block that changed between two packet versions.
 * Used for scrolling to the changed block after undo/redo.
 */
export const getChangedBlockUniqueId = (newPacket, previousPacket) => {
  if (!newPacket || !previousPacket) return null;
  const changedBlockIndex = Object.keys(newPacket?.blocks || {}).find((key) => {
    const newBlock = newPacket.blocks[key];
    const previousBlock = previousPacket.blocks[key];
    if (!newBlock || !previousBlock) return false;
    return JSON.stringify(newBlock) !== JSON.stringify(previousBlock);
  });
  if (changedBlockIndex) {
    return newPacket.blocks[changedBlockIndex]?.unique_id;
  }
  return null;
};

// Deterministic shuffle utilities

/**
 * Simple deterministic pseudorandom number generator (LCG).
 */
const seedRandom = (seed) => {
  let seedValue = 0;
  for (let i = 0; i < seed.length; i++) {
    seedValue = ((seedValue << 5) - seedValue + seed.charCodeAt(i)) & 0xffffffff;
  }
  seedValue = Math.abs(seedValue);

  return () => {
    seedValue = (seedValue * 1664525 + 1013904223) % Math.pow(2, 32);
    return seedValue / Math.pow(2, 32);
  };
};

/**
 * Create a deterministic shuffle of indices [0, 1, 2, ..., length-1].
 * Uses Fisher-Yates shuffle with deterministic random.
 */
export const getDeterministicShuffle = (length, seed) => {
  const rng = seedRandom(seed);
  const indices = Array.from({ length }, (_, i) => i);

  // Fisher-Yates shuffle with deterministic random
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }

  return indices;
};

/**
 * Hook for deterministic randomization based on block and field type.
 * Randomization only resets when items are added/removed, not when edited.
 */
export const useRandomizedIndices = (blockUniqueId, items, fieldType) => {
  return useMemo(() => {
    if (!items || items.length === 0) return [];

    // Create seed from blockId + fieldType + count
    // This way randomization only resets when items are added/removed, not edited
    const seed = `${blockUniqueId}_${fieldType}`;

    return getDeterministicShuffle(items.length, seed);
  }, [blockUniqueId, items?.length, fieldType]);
};
